import React, { useState, useEffect, useRef } from 'react';

const TROEF_OPTIONS = [
  { key: 'hearts', label: '♥', color: '#d32f2f' },
  { key: 'diamonds', label: '♦', color: '#d32f2f' },
  { key: 'clubs', label: '♣', color: '#222' },
  { key: 'spades', label: '♠', color: '#222' },
];

const MAX_ROUNDS = 16;
const MAX_POINTS = 162;

export default function InteractiveScoreboard({ players, game, onNewGameCreated }) {
  const [selectedWij, setSelectedWij] = useState(['', '']);
  const [selectedZij, setSelectedZij] = useState(['', '']);
  const [gameId, setGameId] = useState(null);
  const [rounds, setRounds] = useState(Array.from({ length: MAX_ROUNDS }, () => ({
    wij_points: '', zij_points: '', wij_roem: 0, zij_roem: 0, troef: 'hearts',
  })));
  const [firstRoundTeam, setFirstRoundTeam] = useState('wij');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bij laden van een bestaand potje: haal rondes op
  useEffect(() => {
    if (game && game.id) {
      fetch(`/backend/api/rounds.php?game_id=${game.id}`)
        .then(res => res.json())
        .then(data => {
          const mapped = Array.from({ length: MAX_ROUNDS }, (_, i) => {
            const r = data[i] || {};
            return {
              wij_points: r.wij ?? '',
              zij_points: r.zij ?? '',
              wij_roem: r.wij_roem ?? 0,
              zij_roem: r.zij_roem ?? 0,
              troef: r.troef ?? 'hearts',
            };
          });
          setRounds(mapped);
          setGameId(game.id);
        });
    } else {
      setRounds(Array.from({ length: MAX_ROUNDS }, () => ({
        wij_points: '', zij_points: '', wij_roem: 0, zij_roem: 0, troef: 'hearts',
      })));
      setGameId(null);
    }
  }, [game]);

  // Maak een nieuw potje aan zodra beide teams compleet zijn
  useEffect(() => {
    if (
      selectedWij[0] && selectedWij[1] &&
      selectedZij[0] && selectedZij[1] &&
      !gameId
    ) {
      setSaving(true);
      fetch('/backend/api/games.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player1_id: selectedWij[0],
          player2_id: selectedZij[0],
          player3_id: selectedWij[1],
          player4_id: selectedZij[1],
        })
      })
        .then(res => res.json())
        .then(data => {
          setGameId(data.id);
          setError(null);
        })
        .catch(() => setError('Kon potje niet aanmaken.'))
        .finally(() => setSaving(false));
    }
  }, [selectedWij, selectedZij, gameId]);

  // Sla een ronde op zodra er iets verandert, alleen als beide punten zijn ingevuld
  const saveRound = (roundIdx, roundData) => {
    if (!gameId) return;
    // Alleen opslaan als beide punten velden een getal zijn
    if (
      roundData.wij_points === '' ||
      roundData.zij_points === '' ||
      isNaN(Number(roundData.wij_points)) ||
      isNaN(Number(roundData.zij_points))
    ) {
      console.log('saveRound: niet opgeslagen, incomplete data', roundIdx, roundData);
      return;
    }
    // Bereken nat/pit
    const { nat, pit } = getNatPit(roundData, roundIdx);
    const payload = {
      game_id: gameId,
      round_number: roundIdx + 1,
      wij: Number(roundData.wij_points),
      zij: Number(roundData.zij_points),
      wij_roem: Number(roundData.wij_roem),
      zij_roem: Number(roundData.zij_roem),
      troef: roundData.troef,
      nat: nat.wij || nat.zij ? 1 : 0,
      pit: pit.wij || pit.zij ? 1 : 0
    };
    console.log('saveRound: POST', payload);
    setSaving(true);
    fetch('/backend/api/rounds.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) {
          console.error('saveRound: API error', res.status, res.statusText);
          throw new Error('API error');
        }
        return res.json();
      })
      .then(data => {
        console.log('saveRound: success', data);
        setError(null);
      })
      .catch(e => {
        setError('Kon ronde niet opslaan.');
        console.error('saveRound: fetch error', e);
      })
      .finally(() => setSaving(false));
  };

  // Sla altijd direct op bij wijziging van een ronde
  const prevRoundsRef = useRef(rounds);
  useEffect(() => {
    rounds.forEach((r, i) => {
      // Alleen opslaan als beide puntenvelden zijn ingevuld en gewijzigd t.o.v. vorige state
      if (
        r.wij_points !== '' &&
        r.zij_points !== '' &&
        !isNaN(Number(r.wij_points)) &&
        !isNaN(Number(r.zij_points)) &&
        JSON.stringify(r) !== JSON.stringify(prevRoundsRef.current[i])
      ) {
        saveRound(i, r);
      }
    });
    prevRoundsRef.current = rounds;
  }, [rounds]);

  // Bepaal wie speelt per ronde (om en om)
  const getPlayingTeam = (roundIdx) => {
    if (firstRoundTeam === 'wij') return roundIdx % 2 === 0 ? 'wij' : 'zij';
    return roundIdx % 2 === 0 ? 'zij' : 'wij';
  };

  // NAT/PIT logica (inclusief roem)
  const getNatPit = (r, roundIdx) => {
    const nat = { wij: false, zij: false };
    const pit = { wij: false, zij: false };
    const wijTotaal = Number(r.wij_points || 0) + Number(r.wij_roem || 0);
    const zijTotaal = Number(r.zij_points || 0) + Number(r.zij_roem || 0);
    if (Number(r.wij_points) === 0) pit.wij = true;
    if (Number(r.zij_points) === 0) pit.zij = true;
    const playing = getPlayingTeam(roundIdx);
    const totaal = wijTotaal + zijTotaal;
    if (playing === 'wij' && wijTotaal < totaal / 2) nat.wij = true;
    if (playing === 'zij' && zijTotaal < totaal / 2) nat.zij = true;
    return { nat, pit };
  };

  // Handlers
  const handlePointsChange = (roundIdx, team, value) => {
    let val = value;
    if (val === '') val = '';
    else val = Math.max(0, Math.min(MAX_POINTS, Number(val)));
    const newRounds = [...rounds];
    newRounds[roundIdx][team + '_points'] = val;
    // Automatisch aanvullen andere partij
    if (val !== '' && !isNaN(val)) {
      const otherTeam = team === 'wij' ? 'zij' : 'wij';
      newRounds[roundIdx][otherTeam + '_points'] = MAX_POINTS - Number(val);
      setRounds(newRounds);
      // Sla direct op zodra beide velden zijn gevuld (wat nu altijd het geval is)
      // saveRound(roundIdx, newRounds[roundIdx]); // Verwijder dubbele saveRound-calls
    } else {
      const otherTeam = team === 'wij' ? 'zij' : 'wij';
      newRounds[roundIdx][otherTeam + '_points'] = '';
      setRounds(newRounds);
      // Sla ook op als één veld leeg is (beide leeg = geen opslag)
      if (val !== '') saveRound(roundIdx, newRounds[roundIdx]);
    }
  };
  const handleRoem = (roundIdx, team, amount) => {
    const newRounds = [...rounds];
    newRounds[roundIdx][team + '_roem'] += amount;
    setRounds(newRounds);
    saveRound(roundIdx, newRounds[roundIdx]);
  };
  const handleTroef = (roundIdx, troef) => {
    const newRounds = [...rounds];
    newRounds[roundIdx].troef = troef;
    setRounds(newRounds);
    saveRound(roundIdx, newRounds[roundIdx]);
  };

  // Voeg handler toe voor clear roem
  const handleClearRoem = (roundIdx, team) => {
    const newRounds = [...rounds];
    newRounds[roundIdx][team + '_roem'] = 0;
    setRounds(newRounds);
    saveRound(roundIdx, newRounds[roundIdx]);
  };

  // UI
  return (
    <div style={{ maxWidth: 1300, margin: '0 auto', background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 2px 12px #ccc' }}>
      {game && game.id && (
        <div style={{ fontSize: 22, fontWeight: 'bold', marginBottom: 8 }}>
          Partijnummer: #{game.id}
        </div>
      )}
      {/* Wie speelt de eerste ronde toggle */}
      <div style={{ marginBottom: 12, textAlign: 'center' }}>
        <label style={{ fontSize: 20, fontWeight: 'bold', marginRight: 12 }}>Wie speelt de eerste ronde?</label>
        <select
          value={firstRoundTeam}
          onChange={e => setFirstRoundTeam(e.target.value)}
          style={{ fontSize: 20, padding: '4px 16px', border: '2px solid #222', borderRadius: 8 }}
          disabled={!!gameId}
        >
          <option value="wij">Wij</option>
          <option value="zij">Zij</option>
        </select>
      </div>
      <h2 style={{ fontSize: 32, marginBottom: 8 }}>Scorebord</h2>
      {/* Spelersselectie boven teams */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <select value={selectedWij[0]} onChange={e => setSelectedWij([e.target.value, selectedWij[1]])} style={{ fontSize: 20, marginRight: 8 }} disabled={!!gameId}>
            <option value=''>Kies speler</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={selectedWij[1]} onChange={e => setSelectedWij([selectedWij[0], e.target.value])} style={{ fontSize: 20 }} disabled={!!gameId}>
            <option value=''>Kies speler</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>
          vs
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <select value={selectedZij[0]} onChange={e => setSelectedZij([e.target.value, selectedZij[1]])} style={{ fontSize: 20, marginRight: 8 }} disabled={!!gameId}>
            <option value=''>Kies speler</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={selectedZij[1]} onChange={e => setSelectedZij([selectedZij[0], e.target.value])} style={{ fontSize: 20 }} disabled={!!gameId}>
            <option value=''>Kies speler</option>
            {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      {saving && <div style={{ color: '#1976d2', marginBottom: 8 }}>Opslaan...</div>}
      {/* Scorebord tabel */}
      <table style={{ width: '100%', fontSize: 20, borderCollapse: 'collapse', background: '#fff', marginTop: 8 }}>
        <thead>
          <tr style={{ background: '#f3f3f3', borderBottom: '2px solid #333' }}>
            <th style={{ width: 60, border: '1px solid #888' }}>#</th>
            <th style={{ border: '1px solid #888' }}>Wij</th>
            <th style={{ border: '1px solid #888' }}>Roem</th>
            <th style={{ border: '1px solid #888' }}>Zij</th>
            <th style={{ border: '1px solid #888' }}>Roem</th>
            <th style={{ border: '1px solid #888' }}>Troef</th>
          </tr>
        </thead>
        <tbody>
          {rounds.map((r, i) => {
            const { nat, pit } = getNatPit(r, i);
            const playing = getPlayingTeam(i);
            return (
              <tr key={i}>
                <td style={{ border: '1px solid #888', textAlign: 'center', background: '#f9f9f9' }}>{i + 1}</td>
                <td style={{
                  border: '2px solid #888',
                  textAlign: 'right',
                  background: '#fff',
                  boxShadow: playing === 'wij' ? '0 0 0 3px #1976d2' : undefined,
                  outline: pit.wij ? '3px solid green' : nat.wij ? '3px solid orange' : undefined,
                  backgroundColor: pit.wij ? '#d0ffd0' : nat.wij ? '#ffe0b2' : '#fff',
                }}>
                  <input
                    type="number"
                    value={r.wij_points}
                    onChange={e => handlePointsChange(i, 'wij', e.target.value)}
                    style={{ fontSize: 20, width: 70, padding: 4, border: '1px solid #aaa', borderRadius: 6 }}
                    disabled={!gameId}
                  />
                </td>
                <td style={{ border: '1px solid #888', textAlign: 'center', background: '#f7f7f7' }}>
                  <button type="button" onClick={() => handleRoem(i, 'wij', 20)} style={{ fontSize: 16, marginRight: 4 }} disabled={!gameId}>+20</button>
                  <button type="button" onClick={() => handleRoem(i, 'wij', 50)} style={{ fontSize: 16, marginRight: 4 }} disabled={!gameId}>+50</button>
                  <button type="button" onClick={() => handleClearRoem(i, 'wij')} style={{ fontSize: 14, color: '#d32f2f', marginLeft: 4 }} disabled={!gameId}>Clear</button>
                  <div style={{ fontSize: 16, marginTop: 2 }}>{r.wij_roem}</div>
                </td>
                <td style={{
                  border: '2px solid #888',
                  textAlign: 'right',
                  background: '#fff',
                  boxShadow: playing === 'zij' ? '0 0 0 3px #1976d2' : undefined,
                  outline: pit.zij ? '3px solid green' : nat.zij ? '3px solid orange' : undefined,
                  backgroundColor: pit.zij ? '#d0ffd0' : nat.zij ? '#ffe0b2' : '#fff',
                }}>
                  <input
                    type="number"
                    value={r.zij_points}
                    onChange={e => handlePointsChange(i, 'zij', e.target.value)}
                    style={{ fontSize: 20, width: 70, padding: 4, border: '1px solid #aaa', borderRadius: 6 }}
                    disabled={!gameId}
                  />
                </td>
                <td style={{ border: '1px solid #888', textAlign: 'center', background: '#f7f7f7' }}>
                  <button type="button" onClick={() => handleRoem(i, 'zij', 20)} style={{ fontSize: 16, marginRight: 4 }} disabled={!gameId}>+20</button>
                  <button type="button" onClick={() => handleRoem(i, 'zij', 50)} style={{ fontSize: 16, marginRight: 4 }} disabled={!gameId}>+50</button>
                  <button type="button" onClick={() => handleClearRoem(i, 'zij')} style={{ fontSize: 14, color: '#d32f2f', marginLeft: 4 }} disabled={!gameId}>Clear</button>
                  <div style={{ fontSize: 16, marginTop: 2 }}>{r.zij_roem}</div>
                </td>
                <td style={{ border: '1px solid #888', textAlign: 'center', background: '#fff' }}>
                  <select
                    value={r.troef}
                    onChange={e => handleTroef(i, e.target.value)}
                    style={{ fontSize: 22, fontWeight: 'bold', color: TROEF_OPTIONS.find(opt => opt.key === r.troef)?.color, background: '#fff', border: '1px solid #aaa', borderRadius: 8, padding: '2px 8px' }}
                    disabled={!gameId}
                  >
                    {TROEF_OPTIONS.map(opt => (
                      <option key={opt.key} value={opt.key} style={{ color: opt.color }}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
} 